import { Navigate, useLocation } from "react-router-dom";
import { type ReactNode } from "react";
import { useAuth } from "@/lib/auth";
import { useTenantIdentity } from "@/lib/useTenantIdentity";
import { useUserOrganizations } from "@/lib/org";
import { LoadingScreen } from "@/components/shell/LoadingScreen";

// Phase 6.11.3 - Tenant route guard.
//
// Two modes:
//   requireLink = true  (default): the real portal. Requires an authenticated
//     user who resolves to a LINKED tenant (tenants.user_id = auth.uid()).
//     - not authenticated      -> /login
//     - authenticated, manager  -> /app (wrong portal)
//     - authenticated, no tenant link, no org -> /tenant/welcome (likely mid-invite)
//   requireLink = false: the welcome/set-password landing. Requires only an
//     authenticated session (the invite link establishes one); linking has
//     not happened yet, so we must NOT require a tenant row.
//
// Mirrors ProtectedRoute: wait for queries to fully settle before deciding,
// so routing never flickers or misroutes during auth resolution.

interface TenantRouteProps {
  children: ReactNode;
  requireLink?: boolean;
}

export function TenantRoute({ children, requireLink = true }: TenantRouteProps) {
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();

  const {
    data: tenant,
    isLoading: tenantLoading,
    isFetching: tenantFetching,
    isSuccess: tenantLoaded,
  } = useTenantIdentity();

  const {
    data: memberships,
    isLoading: orgsLoading,
    isFetching: orgsFetching,
    isSuccess: orgsLoaded,
  } = useUserOrganizations();

  if (authLoading) return <LoadingScreen />;

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Welcome/landing: session is enough; do not require a tenant link yet.
  if (!requireLink) {
    return <>{children}</>;
  }

  // Real portal: wait for both identity queries to settle before deciding.
  if (
    tenantLoading || tenantFetching || !tenantLoaded ||
    orgsLoading || orgsFetching || !orgsLoaded
  ) {
    return <LoadingScreen />;
  }

  // Linked tenant -> allow.
  if (tenant) return <>{children}</>;

  // Not a tenant. If they are an org member, send them to the manager app.
  if (memberships && memberships.length > 0) {
    return <Navigate to="/app/dashboard" replace />;
  }

  // Authenticated but neither tenant nor org member: most likely a freshly
  // invited tenant who has not completed linking. Send them to welcome.
  return <Navigate to="/tenant/welcome" replace />;
}
